'use client';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

interface FilterDropdownOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
}

interface BaseProps<T extends string = string> {
  label?: React.ReactNode;
  prefix?: React.ReactNode;
  options: FilterDropdownOption<T>[];
  showChevron?: boolean;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  className?: string;
  contentClassName?: string;
  defaultOpen?: boolean;
}

interface SingleProps<T extends string = string> extends BaseProps<T> {
  multiple?: false;
  value: T;
  onValueChange: (value: T) => void;
}

interface MultiProps<T extends string = string> extends BaseProps<T> {
  multiple: true;
  value: T[];
  onValueChange: (value: T[]) => void;
}

type FilterDropdownProps<T extends string = string> = SingleProps<T> | MultiProps<T>;

function FilterDropdown<T extends string = string>(props: FilterDropdownProps<T>) {
  const {
    label,
    prefix,
    options,
    showChevron = true,
    align = 'start',
    sideOffset = 8,
    className,
    contentClassName,
    defaultOpen,
  } = props;

  const triggerLabel = React.useMemo(() => {
    if (label) return label;
    if (props.multiple) {
      return <>{prefix}</>;
    }
    const selected = options.find((o) => o.value === props.value);
    return (
      <>
        {prefix}
        {selected?.label ?? props.value}
      </>
    );
  }, [label, prefix, options, props]);

  const isSelected = (value: T) =>
    props.multiple ? props.value.includes(value) : props.value === value;

  const handleSelect = (value: T, event: Event) => {
    if (props.multiple) {
      event.preventDefault();
      const next = props.value.includes(value)
        ? props.value.filter((v) => v !== value)
        : [...props.value, value];
      props.onValueChange(next);
    } else {
      props.onValueChange(value);
    }
  };

  return (
    <DropdownMenu.Root defaultOpen={defaultOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-[32px] items-center gap-1 rounded-[10px] bg-v2-btn-secondary pl-3 pr-2 py-2 outline-none',
            'text-[13px] font-medium leading-4 tracking-[-0.13px] whitespace-nowrap text-v2-btn-secondary-text',
            // Hover steps to --v2-border rather than --v2-btn-secondary-hover: in dark
            // the latter jumps #171a1b straight to #3b4447, far too bright for a 32px
            // pill. --v2-border lands on #2a3032 here and #ebebeb in light — the only
            // existing token that gives a gentle step in *both* themes
            // (--v2-btn-secondary-2 equals the resting colour in light, so it would
            // read as no hover at all there). Open keeps the brighter colour, so the
            // three states read rest → hover → open.
            'transition-colors duration-150 hover:bg-v2-border',
            'data-[state=open]:bg-v2-btn-secondary-hover',
            className,
          )}
        >
          <span className="flex items-center gap-1">{triggerLabel}</span>
          {showChevron && (
            <ChevronDown
              size={16}
              strokeWidth={2}
              className="shrink-0 text-v2-btn-secondary-text transition-transform duration-200 data-[state=open]:rotate-180"
            />
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={sideOffset}
          className={cn(
            'z-50 flex min-w-[69px] flex-col gap-4 rounded-[10px] border border-solid border-v2-border bg-v2-btn-secondary p-3',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
            'origin-[var(--radix-dropdown-menu-content-transform-origin)]',
            contentClassName,
          )}
        >
          {options.map((option) => {
            const active = isSelected(option.value);
            return (
              <DropdownMenu.Item
                key={option.value}
                disabled={option.disabled}
                onSelect={(e) => handleSelect(option.value, e)}
                className={cn(
                  'flex cursor-pointer select-none items-center gap-2 rounded-[6px] outline-none',
                  'text-[13px] font-medium leading-4 tracking-[-0.13px] whitespace-nowrap',
                  'transition-colors duration-150',
                  active
                    ? 'text-v2-text-primary'
                    : 'text-v2-text-secondary hover:text-v2-text-primary',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                {props.multiple && (
                  <span
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-solid',
                      active
                        ? 'border-v2-icon-primary-hover bg-transparent text-v2-text-primary'
                        : 'border-v2-icon-primary',
                    )}
                  >
                    {active && <Check size={12} strokeWidth={2.5} />}
                  </span>
                )}
                {option.label}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export type { FilterDropdownOption, FilterDropdownProps };
export { FilterDropdown };
